// src/pages/contracts/components/ContractPDFViewer.js - FIXED VERSION
import React, { useState, useRef, useEffect } from 'react';
import { Download, Upload, X, Maximize2, Minimize2, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api'; // For authenticated requests

const ContractPDFViewer = ({ 
  contract, 
  pdfUrl, 
  onClose, 
  onDownload, 
  onUploadSigned,
  isFullscreen = false,
  onToggleFullscreen,
  isPublic = false,
  showHeader = true,
}) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [pdfError, setPdfError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const fileInputRef = useRef(null);
  const containerRef = useRef(null);

  // Fetch PDF as blob and create object URL for iframe
  useEffect(() => {
    if (pdfUrl) {
      fetchPdfBlob();
    }
    
    // Cleanup function
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [pdfUrl]);

  const fetchPdfBlob = async () => {
    try {
      setLoading(true);
      setPdfError(false);
      
      console.log('Fetching PDF from URL:', pdfUrl, 'isPublic:', isPublic);
      
      let response;
      
      if (isPublic) {
        // ✅ For public page - use fetch directly without authentication
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5076/api';
        const fullUrl = pdfUrl.startsWith('http') ? pdfUrl : `${baseUrl}${pdfUrl}`;
        
        console.log('Public fetch URL:', fullUrl);
        
        response = await fetch(fullUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/pdf',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } else {
        // ✅ For authenticated page - use api service
        console.log('Authenticated fetch URL:', pdfUrl);
        
        // Make sure the URL doesn't have /api prefix if using api service
        const cleanUrl = pdfUrl.startsWith('/api') ? pdfUrl.substring(4) : pdfUrl;
        console.log('Clean URL for API service:', cleanUrl);
        
        response = await api.get(cleanUrl, {
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf',
          },
        });

        if (!response.data) {
          throw new Error('No data received from server');
        }

        // Create blob URL from response data
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setPdfError(true);
      setLoading(false);
      
      // Show specific error message based on error type
      if (error.response?.status === 401) {
        toast.error('Sesiunea a expirat. Te rugăm să te conectezi din nou.');
      } else if (error.response?.status === 404) {
        toast.error('PDF-ul nu a fost găsit. Verifică dacă documentul a fost generat.');
      } else if (error.response?.status === 403) {
        toast.error('Nu ai permisiuni pentru a accesa acest document.');
      } else {
        console.error('Full error details:', {
          message: error.message,
          response: error.response,
          config: error.config
        });
        toast.error('Eroare la încărcarea PDF-ului: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleReset = () => {
    setZoom(100);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (e.target.tagName === 'IFRAME' || e.target.tagName === 'OBJECT' || e.target.tagName === 'EMBED') return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Doar fișierele PDF sunt acceptate');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Fișierul nu poate depăși 10MB');
        return;
      }

      onUploadSigned(file);
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canUploadSigned = contract && 
    (contract.status === 'Accessed' || contract.status === 'Sent') &&
    !contract.signedPdfFilePath &&
    onUploadSigned;

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50' : 'relative'} bg-gray-900`}>
      {showHeader && (
      <div className="flex items-center justify-between p-4 bg-white border-b">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Contract {contract?.number || contract?.contractNumber}
          </h2>
          {contract && (
            <span className="text-sm text-gray-600">
              {contract.clientName}
            </span>
          )}
          {isPublic && (
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
              Acces Public
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Debug Info - only in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="text-xs text-gray-500 max-w-xs truncate">
              URL: {pdfUrl} | Blob: {blobUrl ? 'Yes' : 'No'} | {isPublic ? '(public)' : '(auth)'}
            </div>
          )}

          {/* Zoom Controls */}
          <div className="flex items-center space-x-1 border rounded-lg p-1">
            <button
              onClick={handleZoomOut}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Micșorează"
              disabled={zoom <= 25}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="px-2 text-sm font-medium min-w-[3rem] text-center">
              {zoom}%
            </span>
            <button
              onClick={handleZoomIn}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Mărește"
              disabled={zoom >= 300}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          {/* Rotate */}
          <button
            onClick={handleRotate}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Rotește"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>

          {/* Download */}
          {onDownload && (
            <button
              onClick={onDownload}
              className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Descarcă
            </button>
          )}

          {/* Upload Signed */}
          {canUploadSigned && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Upload className="w-4 h-4 mr-2" />
                Încarcă Semnat
              </button>
            </>
          )}

          {/* Fullscreen Toggle */}
          {onToggleFullscreen && (
            <button
              onClick={onToggleFullscreen}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title={isFullscreen ? "Ieși din fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </button>
          )}

          {/* Close */}
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Închide"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      )}


      {/* PDF Content */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-hidden relative bg-gray-800"
        style={{ height: isFullscreen ? 'calc(100vh - 73px)' : '600px' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Se încarcă documentul...</p>
              <p className="text-sm text-gray-400 mt-2">
                {isPublic ? 'Acces public' : 'Acces autentificat'}
              </p>
            </div>
          </div>
        ) : pdfError ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white max-w-md px-4">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Eroare la încărcarea PDF-ului</h3>
              <p className="text-gray-300 mb-4">
                Nu s-a putut încărca documentul pentru vizualizare. Poți să îl descarci direct pentru a-l vedea.
              </p>
              <p className="text-xs text-gray-400 mb-4">
                URL: {pdfUrl} {isPublic ? '(public)' : '(auth)'}
              </p>
              {onDownload && (
                <button
                  onClick={onDownload}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 inline-flex items-center transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descarcă PDF
                </button>
              )}
              <div className="mt-4">
                <button
                  onClick={() => {
                    console.log('Retry clicked, current state:', { pdfUrl, isPublic, blobUrl });
                    fetchPdfBlob();
                  }}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Încearcă din nou
                </button>
              </div>
            </div>
          </div>
        ) : blobUrl ? (
          <div 
            className="w-full h-full flex items-center justify-center"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) scale(${zoom / 100}) rotate(${rotation}deg)`,
              cursor: isDragging ? 'grabbing' : 'grab',
              transition: isDragging ? 'none' : 'transform 0.2s ease'
            }}
          >
            {/* Primary PDF viewer - use embed for better compatibility */}
            <embed
              src={`${blobUrl}#toolbar=0`}
              type="application/pdf"
              className="w-full h-full border-0 bg-white shadow-lg"
              style={{ 
                pointerEvents: isDragging ? 'none' : 'auto',
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Download className="w-8 h-8" />
              </div>
              <h3 className="text-lg font-semibold mb-2">PDF nu este disponibil</h3>
              <p className="text-gray-300 mb-4">
                Documentul nu a fost generat încă sau nu este disponibil pentru vizualizare.
              </p>
              {contract?.status === 'Draft' && (
                <p className="text-sm text-gray-400">
                  Generează PDF-ul pentru a putea vizualiza contractul.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-t text-sm text-gray-600">
        <div className="flex items-center space-x-4">
          <span>Zoom: {zoom}%</span>
          <span>Rotație: {rotation}°</span>
          {contract?.signedPdfFilePath && (
            <span className="text-green-600 font-medium">
              ✓ Document semnat disponibil
            </span>
          )}
          {pdfError && (
            <span className="text-red-600 font-medium">
              ⚠ Eroare la încărcare
            </span>
          )}
          {loading && (
            <span className="text-blue-600 font-medium">
              ⏳ Se încarcă...
            </span>
          )}
          {blobUrl && (
            <span className="text-green-600 font-medium">
              ✓ PDF încărcat
            </span>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-xs">
            {pdfError 
              ? 'Folosește butonul Descarcă pentru a salva PDF-ul' 
              : 'Folosește mouse-ul pentru a naviga și zoom'
            }
          </span>
        </div>
      </div>
    </div>
  );
};

export default ContractPDFViewer;